import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import apiClient from '@/lib/api';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await apiClient.post('/auth/forgot-password', { email });
      setSubmitted(true);
    } catch (err) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pista-glow pointer-events-none" />
      
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-2 tracking-tight">
            <span className="text-pista">Lost</span>DrkC
          </h1>
          <p className="text-muted-foreground text-sm">Password Recovery</p>
        </div>

        {submitted ? (
          <Card data-testid="reset-sent-card" className="border-border/60 backdrop-blur-sm bg-card/95">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-pista/20 mb-4">
                  <CheckCircle className="h-7 w-7 text-pista" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Check your email</h3>
                <p className="text-muted-foreground text-sm mb-6">
                  If an account exists for <strong className="text-foreground">{email}</strong>, we've sent a password reset link.
                </p>
                <Link to="/login">
                  <Button variant="outline" className="w-full" data-testid="back-to-login-button">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Sign in
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="forgot-password-card" className="border-border/60 backdrop-blur-sm bg-card/95">
            <CardHeader>
              <CardTitle className="text-2xl font-bold">Forgot password?</CardTitle>
              <CardDescription>
                Enter your email and we'll send you a reset link
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      placeholder="you@example.com"
                      className="pl-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                  data-testid="send-reset-button"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <Link to="/login" className="block text-center text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-login-link">
                  <ArrowLeft className="inline mr-1 h-3 w-3" />
                  Back to Sign in
                </Link>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
